import { Dialog } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum, User_Role_Enum } from '@kheops/graphql';
import useOpenable from '../../hooks/useOpenable';
import { currentContextAtom, userAtom } from '../../state';
import { RoutePaths } from '../../routes/AppRoutes';
import { canEditDeliveryZonesAtom, canEditServicesAtom } from '../../company/state';
import { brandsAtom } from '../../brands/state';
import { administrativeAreasAtom } from '../../administrativeAreas/state';
import ProductFamiliesSettings from './ProductFamiliesSettings';
import SettingsDialogWelcome from './SettingsDialogWelcome';
import DeliveryZoneSettings from './DeliveryZoneSettings';
import ContactInformationSettings from './ContactInformationSettings';
import SettingsSaved from './SettingsSaved';
import BrandsSettings from './BrandsSettings';
import AdministrativeAreasSettings from './AdministrativeAreasSettings';
import ServicesSettings from './ServicesSettings';

export interface NextOptions {
  skipped: boolean;
  noRedirect?: boolean;
}

export interface SettingsDialogViewProps {
  next: (options?: NextOptions) => void;
  standAlone?: boolean;
  submitLabel?: string;
}

export default function SettingsDialog(): React.JSX.Element {
  const navigate = useNavigate();
  const user = useAtomValue(userAtom);
  const context = useAtomValue(currentContextAtom);
  const { open, close, isOpen } = useOpenable();
  const [views, setViews] = useState<React.JSX.Element[]>([]);
  const [viewIndex, setViewIndex] = useState(0);
  const skippedSteps = useRef(0);
  const viewIndexRef = useRef(0);
  const maxViewIndexRef = useRef(0);
  const { t } = useTranslation(['common', 'settings']);
  const canEditDeliveryZones = useAtomValue(canEditDeliveryZonesAtom);
  const canEditServices = useAtomValue(canEditServicesAtom);
  const isSSROrSSA = [User_Role_Enum.SupplierSalesAdmin, User_Role_Enum.SupplierSalesRep].includes(context.roleName);
  const brands = useAtomValue(brandsAtom);
  const administrativeAreas = useAtomValue(administrativeAreasAtom);

  const next = (options?: NextOptions): void => {
    if (options?.skipped) {
      skippedSteps.current += 1;
    }

    if (viewIndexRef.current === maxViewIndexRef.current - 1
      || (viewIndexRef.current === maxViewIndexRef.current - 2 && skippedSteps.current)
    ) {
      close();

      if (!options?.noRedirect && skippedSteps.current) {
        navigate(generatePath(RoutePaths.USER));
      }
    } else {
      viewIndexRef.current += 1;
      setViewIndex(viewIndexRef.current);
    }
  };

  useEffect((): void => {
    if (views.length) {
      return;
    }
    setViewIndex(0);

    const settingsViews: React.JSX.Element[] = [];

    const shouldDisplayDeliveryZoneSettings = canEditDeliveryZones && !context.deliveryZones?.length && context.realm === Business_Profile_Enum.Supplier;
    const shouldDisplayServicesSettings = canEditServices && context.realm === Business_Profile_Enum.Supplier && Object.values(context.companyServices).every((service) => {
      return service === null || (Array.isArray(service) && service.length === 0);
    });
    const shouldDisplayBrandSettings = isSSROrSSA && !context.userBrands?.length;
    const shouldDisplayAdministrativeAreasSettings = isSSROrSSA && !context.userAdministrativeAreas?.length;
    const shouldDisplayProductFamiliesSettings = !context.productFamilies?.length && context.realm === Business_Profile_Enum.Buyer && context.roleName === User_Role_Enum.BuyerManager;

    if (shouldDisplayServicesSettings) {
      settingsViews.unshift(<ServicesSettings next={next} close={close} />)
    }

    if (shouldDisplayAdministrativeAreasSettings) {
      settingsViews.unshift(<AdministrativeAreasSettings next={next} areas={administrativeAreas} />);
    }

    if (shouldDisplayBrandSettings) {
      settingsViews.unshift(
        <BrandsSettings
          next={next}
          brands={brands}
          submitLabel={t(settingsViews.length ? 'common:next' : 'common:validate')}
        />,
      );
    }

    if (shouldDisplayProductFamiliesSettings) {
      settingsViews.unshift(<ProductFamiliesSettings next={next} />);
    }

    if (shouldDisplayDeliveryZoneSettings) {
      settingsViews.unshift(
        <DeliveryZoneSettings
          next={next}
          submitLabel={t(settingsViews.length ? 'common:next' : 'common:validate')}
        />,
      );
    }

    if (!user.contactInformation) {
      settingsViews.unshift(
        <ContactInformationSettings
          next={next}
          submitLabel={t(settingsViews.length ? 'common:next' : 'common:validate')}
          cancelLabel={t('settings:complete_later')}
        />,
      );
    }

    settingsViews.unshift(<SettingsDialogWelcome next={next} />);
    settingsViews.push(<SettingsSaved next={next} />);

    setViews(settingsViews);

    // Check if there's more views than the welcome and the final ones
    if (settingsViews.length > 2) {
      maxViewIndexRef.current = settingsViews.length;
      open();
    }
  }, [views]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {!!views.length && (
        <Dialog
          open={isOpen}
          onClose={(): void => { /* noop */ }}
          sx={{
            mx: {
              xs: 2,
              sm: 0,
            },
            '& .MuiPaper-root.MuiDialog-paper': {
              width: 640,
              minWidth: {
                xs: 'calc(100vw - 64px)',
                sm: 640,
              },
              maxHeight: {
                xs: 'calc(100vh - 64px)',
                sm: 'calc(100vh - 112px)',
                lg: 'calc(100vh - 124px)',
              },
            },
          }}
        >
          {views[viewIndex]}
        </Dialog>
      )}
    </>
  );
}
